import React, { useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import AddIcon from "@mui/icons-material/Add";
import { columns } from "./columns";

import RegularButton from "components/CustomButtons/Button";
import { Home } from "@mui/icons-material";
import { useSearch } from "hooks/useSearch";
import { ModalV2 } from "components/Modal/ModalV2";
import { Modal } from "components/Modal/Modal";
import { APARTMENTS_PHRASE } from "./config";
import { ApartmentsForm } from "./ApartmentsForm";
import {
  fetchApartments,
  newApartment,
  clearApartments,
  updateApartmentStatus,
  handleApartmentsInputsAction,
} from "redux/actions/buildings/apartments/apartmentsActions";
import { fetchFacilities } from "redux/actions/buildings/facilities/facilitiesActions";
import { useToggle } from "hooks/useToggle";
import { AssignMeterForm } from "./AssignMeterForm";
import { AssignCustomerForm } from "./AssignCustomerForm";
import { AssignContractForm } from "./AssingContractForm";

const LOADINGS = [
  "fetchApartments",
  "fetchCustomers",
  "fetchNewApartment",
  "fetchEditApartment",
  "loadingServiceStatus"
];

const SET_SERVICE_TO_DISCONNECT_STATUS = "CORTAR";
const SET_SERVICE_TO_CONNECT_STATUS = "RECONECTAR"

export const ApartmentsPage = () => {
  const dispatch = useDispatch();
  const [isOpen, handleIsOpen] = useToggle();
  const [isMeterOpen, handleOpenMeter] = useToggle();
  const [isCustomerOpen, handleOpenCustomer] = useToggle();
  const [isContractOpen, handleOpenContract] = useToggle();
  const [isServiceOffOpen, handleServiceOff] = useToggle();
  const [isServiceOnOpen, handleServiceOn] = useToggle();

  const { rules } = useSelector(({ login }) => login)

  const {
    setSnackbar,
    clearSearch,
    buildCompLoadings
  } = useSearch();
  const { isLoadingComp } = useSelector(
    state => state.search
  );
  const { apartmentList, editApartment, isAddMode, isDeleteMode, station } = useSelector(state => state.apartments);

  const apartmentsMemo = useMemo(
    () =>
      apartmentList?.map(apartment => ({
        ...apartment,
        modal: handleIsOpen,
        meterModal: handleOpenMeter,
        customerModal: handleOpenCustomer,
        contractModal: handleOpenContract,
        serviceOffModal: handleServiceOff,
        serviceOnModal: handleServiceOn,
        rules
      })),
    [apartmentList]
  );

  const getApartments = useCallback(
    stationID => {
      dispatch(fetchApartments(stationID));
    },
    [dispatch]
  );

  const getFacilities = useCallback(
    stationID => {
      dispatch(fetchFacilities(stationID));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    if (station.length < 1) {
      setSnackbar(APARTMENTS_PHRASE.search.messages.noStations);
      return;
    }
    const stationID = station.id;
    getApartments(stationID);
    getFacilities(stationID);
  }, [station, setSnackbar]);

  const submitMemoized = useCallback((e) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handleClose = () => {
    handleIsOpen();
  };

  const handleMeterClose = () => {
    handleOpenMeter();
  };

  const handleCustomerClose = () => {
    handleOpenCustomer();
  };

  const handleOpenAdd = () => {
    dispatch(newApartment());
    handleIsOpen();
  };

  useEffect(() => {
    clearSearch();
    dispatch(clearApartments());
  }, [clearSearch]);

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, []);

  const handleStatusChange = (status, callback) => {
    dispatch(updateApartmentStatus(editApartment.id, status, callback))
    setSnackbar(APARTMENTS_PHRASE.modal.responses.status, 'success')
  }

  return (
    <React.Fragment>
      <CardTable
        data={apartmentsMemo}
        columns={columns}
        loading={isLoadingComp.fetchApartments}
        title={APARTMENTS_PHRASE.page.table.title}
        icon={<Home />}
        hasHeader
        blockBtn
        hasControl
        controls={
          true && (
            <RegularButton
              round
              justIcon
              color="info"
              size="sm"
              onClick={handleOpenAdd}
            >
              <AddIcon style={{ margin: 0 }} />
            </RegularButton>
          )
        }
        tableContent={
          <TableInputs
            reducer="apartments"
            stations
            dispatchAction={handleApartmentsInputsAction}
            submitAction={submitMemoized}
          />
        }
      />
      <ModalV2
        open={isMeterOpen}
        onClose={handleMeterClose}
        toRight
        title={APARTMENTS_PHRASE.meterModal.title}
      >
        {<AssignMeterForm handleClose={handleMeterClose} />}
      </ModalV2>
      <ModalV2
        open={isCustomerOpen}
        onClose={handleCustomerClose}
        toRight
        title={APARTMENTS_PHRASE.customerModal.title}
      >
        {<AssignCustomerForm handleClose={handleCustomerClose} />}
      </ModalV2>
      <ModalV2 
      open={isContractOpen} 
      onClose={handleOpenContract} 
      toRight
      title={APARTMENTS_PHRASE.contractModal.title}
      >
        {<AssignContractForm handleClose={handleOpenContract} />}
      </ModalV2>
      <ModalV2
        open={isOpen}
        onClose={handleClose}
        toRight
        title={
          isAddMode
            ? APARTMENTS_PHRASE.modal.titles.add
            : isDeleteMode
              ? APARTMENTS_PHRASE.modal.titles.delete
              : APARTMENTS_PHRASE.modal.titles.edit
        }
      >
        {<ApartmentsForm handleClose={handleClose} />}
      </ModalV2>
      <Modal
        isOpen={isServiceOffOpen}
        handleClose={handleServiceOff}
        textButton1={APARTMENTS_PHRASE.modal.actions.serviceOff}
        textButton2={APARTMENTS_PHRASE.modal.actions.cancel}
        actionFunction1={handleServiceOff}
        actionFunction2={() => handleStatusChange(SET_SERVICE_TO_DISCONNECT_STATUS, handleServiceOff)}
        textContent={APARTMENTS_PHRASE.modal.titles.serviceOff}
        isDisabled={isLoadingComp.loadingServiceStatus}
      />
      <Modal
        isOpen={isServiceOnOpen}
        handleClose={handleServiceOn}
        textButton1={APARTMENTS_PHRASE.modal.actions.serviceOn}
        textButton2={APARTMENTS_PHRASE.modal.actions.cancel}
        actionFunction1={handleServiceOn}
        actionFunction2={() => handleStatusChange(SET_SERVICE_TO_CONNECT_STATUS, handleServiceOn)}
        textContent={APARTMENTS_PHRASE.modal.titles.serviceOn}
        isDisabled={isLoadingComp.loadingServiceStatus}
      />
    </React.Fragment>
  );
};
